import * as R from 'ramda'
import { Exam } from 'types/exams'
import { FreeTrialReducer } from 'types/reducers'

type BookIsFreeTrialFlag = FreeTrialReducer['bookIsFreeTrialFlags'][number]

export const isBookInFreeTrial = (
  bookIsFreeTrialFlags: BookIsFreeTrialFlag[],
  book_id: string
): boolean => {
  if (R.isEmpty(book_id)) {
    return false
  }

  return R.pipe(
    R.find(R.propEq('book_id', book_id)),
    R.propOr(false, 'is_free_trial'),
    R.equals(true)
  )(bookIsFreeTrialFlags)
}

export const isChapterInFreeTrial = (
  bookIsFreeTrialFlags: BookIsFreeTrialFlag[],
  chapter_id: string
): boolean => {
  if (R.isEmpty(chapter_id)) {
    return false
  }

  return bookIsFreeTrialFlags.some(flag => {
    const chapterFlag = flag as BookIsFreeTrialFlag & {
      original_first_chapter_id?: string
    }

    return (
      flag.is_free_trial && chapterFlag.original_first_chapter_id === chapter_id
    )
  })
}

const isExamMatchedByFreeTrialExamId = (
  exam: Exam,
  booksFlaggedAsFreeTrial: BookIsFreeTrialFlag[]
): boolean => {
  const studentExamId = R.propOr('', 'id', exam)
  const examId = R.propOr('', 'exam_id', exam)

  if (R.isEmpty(studentExamId) && R.isEmpty(examId)) {
    return false
  }

  return booksFlaggedAsFreeTrial.some(
    flag =>
      flag.is_free_trial &&
      (flag.free_trial_exam_id === studentExamId ||
        flag.free_trial_exam_id === examId)
  )
}

const isExamMarkedAvailableByApi = (exam: Exam): boolean => {
  if (R.propOr(false, 'is_free_trial', exam)) {
    return true
  }

  if (R.propOr(false, 'is_free_trial_chapter', exam)) {
    return true
  }

  if (R.propOr(false, 'free_trial_featured_exam', exam)) {
    return true
  }

  return R.propOr(true, 'is_available', exam) !== false
}

export const isExamIncludedInFreeTrial = (
  exam: Exam | null | undefined,
  booksFlaggedAsFreeTrial: BookIsFreeTrialFlag[]
): boolean => {
  if (R.isNil(exam)) {
    return false
  }

  if (isExamMarkedAvailableByApi(exam)) {
    return true
  }

  if (isExamMatchedByFreeTrialExamId(exam, booksFlaggedAsFreeTrial)) {
    return true
  }

  const attachedBookId = String(
    (exam as Exam & { original_attached_id?: string; book_id?: string })
      .original_attached_id ||
      (exam as Exam & { book_id?: string }).book_id ||
      ''
  )

  const isExamAvailableInFreeTrialBookLevel: boolean =
    (R.propOr('', 'attached_type', exam) === 'book' ||
      R.isEmpty(R.propOr('', 'attached_type', exam))) &&
    isBookInFreeTrial(booksFlaggedAsFreeTrial, attachedBookId)

  const isExamAvailableInFreeTrialChapterLevel: boolean =
    R.propOr('', 'attached_type', exam) === 'chapter' &&
    isChapterInFreeTrial(booksFlaggedAsFreeTrial, attachedBookId)

  const isExamAvailableInFreeTrialCourseLevel: boolean =
    R.propOr('', 'attached_type', exam) === 'course' &&
    R.propOr(false, 'is_free_trial', exam)

  return (
    isExamAvailableInFreeTrialBookLevel ||
    isExamAvailableInFreeTrialChapterLevel ||
    isExamAvailableInFreeTrialCourseLevel
  )
}
